// Razorpay checkout loader and modal-opener.
//
// Uses the official checkout.js script (https://checkout.razorpay.com/v1/checkout.js).
// We load it lazily on first use so the bundle stays small and first-paint
// of /login + /signup isn't gated on a third-party request.
//
// Frontend Razorpay key
// ----------------------
// The browser cannot read backend env vars. Vite only exposes variables
// prefixed with VITE_ to client code via import.meta.env. The publishable
// Razorpay key id is therefore read from VITE_RAZORPAY_KEY_ID (set in
// frontend/.env). DO NOT read process.env.RAZORPAY_KEY_ID, plain
// import.meta.env.RAZORPAY_KEY_ID, or any unprefixed name — those resolve
// to undefined in the browser bundle and produce the generic Razorpay
// "Uh! oh! Something went wrong" overlay.
//
// SECURITY: never reference RAZORPAY_KEY_SECRET or RAZORPAY_WEBHOOK_SECRET
// from frontend code. They're backend-only. Anything in VITE_* is baked
// into the browser bundle and visible to every visitor.
//
// `openRazorpayCheckout` returns a Promise that resolves with the
// `{ razorpay_payment_id, razorpay_order_id, razorpay_signature }` triple
// on success, or rejects with one of:
//   - { code: 'CHECKOUT_DISMISSED' }      — user closed the modal
//   - { code: 'CHECKOUT_FAILED', error }  — Razorpay payment.failed
//   - { code: 'CHECKOUT_LOAD_FAILED' }    — script failed to load / window.Razorpay
//                                           missing / new Razorpay() threw
//   - { code: 'CHECKOUT_MISSING_KEY' }    — VITE_RAZORPAY_KEY_ID not set
//   - { code: 'CHECKOUT_BAD_ORDER_ID' }   — order id missing or wrong shape
//   - { code: 'CHECKOUT_BAD_AMOUNT' }     — amount missing / not positive int
//   - { code: 'CHECKOUT_BAD_CURRENCY' }   — currency != 'INR'
//
// Callers translate these via describeCheckoutError() into popup copy.

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
const SCRIPT_ID = 'razorpay-checkout-script';

// Resolve the publishable Razorpay key id from Vite's import.meta.env at
// module-load time. The value is a string in the bundle; nothing is read
// from window or document. If the variable is unset, this is the empty
// string, which the preflight rejects with CHECKOUT_MISSING_KEY.
export function getFrontendRazorpayKey() {
  try {
    const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
    const k = env.VITE_RAZORPAY_KEY_ID;
    return typeof k === 'string' ? k.trim() : '';
  } catch (_e) {
    return '';
  }
}

// Optional: 'test' | 'live' | 'mock' for UI-only display purposes.
export function getFrontendRazorpayEnv() {
  try {
    const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
    const v = env.VITE_RAZORPAY_ENV;
    return typeof v === 'string' && v.length > 0 ? v : 'test';
  } catch (_e) {
    return 'test';
  }
}

// Optional override for the merchant-logo image displayed inside the
// Razorpay checkout modal. When NOT set, Razorpay falls back to the URL
// you've configured in the Razorpay dashboard (Settings → Business →
// Logo). If that dashboard URL points at something the browser can't
// reach (typical local-dev pitfall: a `http://localhost:NNNN/...` URL
// auto-suggested when you put localhost as your business website),
// checkout.js fails to load it and surfaces the generic
// "Uh! oh! Something went wrong / Error in opening checkout" overlay.
//
// Set VITE_RAZORPAY_CHECKOUT_LOGO_URL in frontend/.env to a public HTTPS
// URL (anything reachable from the browser) to override that broken
// dashboard default during local testing — e.g. point at a public CDN
// URL or your hosted brand asset. Restart Vite after editing .env.
//
// Anything other than an https:// URL is rejected so we don't propagate
// a misconfigured value into the checkout.
export function getFrontendCheckoutLogo() {
  try {
    const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
    const v = env.VITE_RAZORPAY_CHECKOUT_LOGO_URL;
    if (typeof v !== 'string') return '';
    const trimmed = v.trim();
    if (!trimmed) return '';
    // Only accept https URLs. Razorpay's checkout iframe is https-served
    // so any http logo would be blocked as mixed content; rejecting here
    // gives a cleaner failure mode than a runtime image load error.
    if (!/^https:\/\//i.test(trimmed)) return '';
    return trimmed;
  } catch (_e) {
    return '';
  }
}

// True when `url` resolves to a host that the Razorpay checkout iframe
// (origin https://api.razorpay.com) cannot fetch from a user's browser:
// loopback, private RFC1918 ranges, or anything with no parseable host.
// Used by sanitizeRazorpayOptions() to scrub `image` / `callback_url`
// before they reach `new Razorpay(options)` — defense in depth against
// a future caller (or env override) accidentally pointing those fields
// at a dev-only host. The Razorpay merchant logo URL configured on the
// dashboard is fetched server-side by Razorpay and is OUTSIDE this
// sanitizer's scope; that one has to be fixed in the dashboard itself.
function isLocalOrPrivateUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  // Anything without a `scheme:` prefix is a relative URL and resolves
  // against the Razorpay iframe's origin — almost certainly a bug.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return true;
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (_e) {
    return true;
  }
  const host = (parsed.hostname || '').toLowerCase();
  if (!host) return true;
  if (host === 'localhost' || host === '0.0.0.0' || host === '::1') return true;
  if (host.startsWith('127.')) return true;
  if (host.startsWith('10.')) return true;
  if (host.startsWith('192.168.')) return true;
  // 172.16.0.0 – 172.31.255.255
  const m172 = host.match(/^172\.(\d{1,3})\./);
  if (m172) {
    const n = Number(m172[1]);
    if (n >= 16 && n <= 31) return true;
  }
  return false;
}

// Final guard run immediately before `new Razorpay(options)`. Strips
// fields that, if pointing at a local/private URL, would either trip
// browser CORS in the Razorpay iframe (image) or bounce the user back
// to a dev-only redirect (callback_url + redirect). We log only the
// shape of what was removed — never the URL itself, since logos can
// inadvertently be PII-bearing avatars on some accounts.
function sanitizeRazorpayOptions(options) {
  const sanitized = { ...options };
  let removedImage = false;
  let removedCallback = false;

  if (isLocalOrPrivateUrl(sanitized.image)) {
    delete sanitized.image;
    removedImage = true;
  }

  if (isLocalOrPrivateUrl(sanitized.callback_url)) {
    delete sanitized.callback_url;
    delete sanitized.redirect;
    removedCallback = true;
  }

  // `redirect: true` only makes sense paired with a callback_url.
  if (sanitized.redirect === true && !sanitized.callback_url) {
    delete sanitized.redirect;
  }

  if (typeof console !== 'undefined' && console.warn) {
    if (removedImage) {
      console.warn('Removed unsafe Razorpay image URL for local checkout');
    }
    if (removedCallback) {
      console.warn('Removed unsafe Razorpay callback_url for local checkout');
    }
  }

  return sanitized;
}

// Synchronous preflight that catches the most common reasons Razorpay's
// checkout.js silently fails to open. Returning a structured code lets
// callers map each cause to a specific Modal.error message instead of
// the generic Razorpay overlay.
function preflightCheckoutOptions({ keyId, orderId, amountPaise, currency }) {
  if (!keyId || typeof keyId !== 'string' || keyId.length === 0) {
    return { ok: false, code: 'CHECKOUT_MISSING_KEY' };
  }
  if (
    !orderId ||
    typeof orderId !== 'string' ||
    !/^order_[A-Za-z0-9_]+$/.test(orderId)
  ) {
    return { ok: false, code: 'CHECKOUT_BAD_ORDER_ID' };
  }
  if (!Number.isInteger(amountPaise) || amountPaise <= 0) {
    return { ok: false, code: 'CHECKOUT_BAD_AMOUNT' };
  }
  if (currency !== 'INR') {
    return { ok: false, code: 'CHECKOUT_BAD_CURRENCY' };
  }
  return { ok: true };
}

let loadingPromise = null;

export function loadRazorpayCheckout() {
  if (typeof window !== 'undefined' && window.Razorpay) {
    return Promise.resolve(window.Razorpay);
  }
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject({ code: 'CHECKOUT_LOAD_FAILED', error: new Error('document unavailable') });
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Razorpay), { once: true });
      existing.addEventListener(
        'error',
        () => {
          loadingPromise = null;
          reject({ code: 'CHECKOUT_LOAD_FAILED', error: new Error('script tag error event') });
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (window.Razorpay) {
        resolve(window.Razorpay);
      } else {
        loadingPromise = null;
        reject({ code: 'CHECKOUT_LOAD_FAILED', error: new Error('window.Razorpay missing') });
      }
    };
    script.onerror = (err) => {
      loadingPromise = null;
      reject({ code: 'CHECKOUT_LOAD_FAILED', error: err });
    };
    document.head.appendChild(script);
  });

  return loadingPromise;
}

export async function openRazorpayCheckout({
  // `keyId` from the backend response is intentionally accepted but
  // ignored when VITE_RAZORPAY_KEY_ID is set. Keeping it in the signature
  // means existing callsites don't break, and during Vite dev/prod the
  // frontend env is the single source of truth (so a missing client key
  // surfaces an actionable error instead of leaking the backend value).
  keyId, // eslint-disable-line no-unused-vars
  orderId,
  amountPaise,
  currency = 'INR',
  name = 'Glass-i',
  description,
  prefill = {},
  notes = {},
  themeColor = '#1877F2'
}) {
  // Resolve the canonical client-side key from Vite env.
  const effectiveKeyId = getFrontendRazorpayKey();

  // Run all preflight checks BEFORE loading the script — no point fetching
  // checkout.js if we already know the options are invalid. This swaps the
  // useless "Uh! oh! Something went wrong" overlay for an actionable popup.
  const pre = preflightCheckoutOptions({
    keyId: effectiveKeyId,
    orderId,
    amountPaise,
    currency
  });
  if (!pre.ok) {
    throw { code: pre.code };
  }

  const Razorpay = await loadRazorpayCheckout();

  return new Promise((resolve, reject) => {
    // Settle-once guard. Some checkout.js builds fire `ondismiss` AFTER a
    // successful `handler()` call (modal closes itself, then dispatches
    // the dismiss event for cleanup). Without this guard the second call
    // is a no-op on the Promise — but it would log a misleading rejection
    // and confuse callers trying to debug. Track the state explicitly.
    let settled = false;
    const settleResolve = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const settleReject = (reason) => {
      if (settled) return;
      settled = true;
      reject(reason);
    };

    // Override the merchant-logo image when an env override is set. This
    // is the local-dev workaround for a Razorpay dashboard whose default
    // logo URL points at an unreachable host (e.g. http://localhost:7070/...
    // auto-filled from a localhost website setting on signup), which
    // otherwise causes checkout.js to fail with "Error in opening checkout".
    const checkoutLogo = getFrontendCheckoutLogo();

    const options = {
      key: effectiveKeyId,
      amount: amountPaise,
      currency,
      name,
      description: description || '',
      order_id: orderId,
      // Only set `image` when explicitly overridden — omitting it leaves
      // Razorpay free to use the dashboard default in production where
      // that URL is presumed valid.
      ...(checkoutLogo ? { image: checkoutLogo } : {}),
      prefill: {
        name: prefill.name || '',
        email: prefill.email || '',
        contact: prefill.contact || ''
      },
      notes: notes || {},
      theme: { color: themeColor },
      // We verify the signature server-side after handler() resolves,
      // so don't let Razorpay open a redirect — keep the SPA flow.
      handler: (response) => {
        // Razorpay returns: { razorpay_payment_id, razorpay_order_id,
        // razorpay_signature }. Nothing is logged — payment identifiers
        // must never be written to the browser console.
        settleResolve({
          razorpayPaymentId: response && response.razorpay_payment_id,
          razorpayOrderId: response && response.razorpay_order_id,
          razorpaySignature: response && response.razorpay_signature
        });
      },
      modal: {
        ondismiss: () => {
          // Only treat as a real dismiss if no success/failure has fired
          // yet. After handler() resolves, the settle guard makes this a
          // no-op so we don't surface a spurious "cancelled" error.
          settleReject({ code: 'CHECKOUT_DISMISSED' });
        },
        confirm_close: true,
        escape: true
      }
    };

    // Final guard before handing options to checkout.js. Strips any
    // image / callback_url that resolves to a local or private host so
    // checkout never tries to fetch a logo from localhost (which would
    // surface the generic "Uh! oh!" overlay due to a CORS/loopback
    // block) and never bounces the SPA to a dev redirect URL.
    const safeOptions = sanitizeRazorpayOptions(options);

    let rzp;
    try {
      rzp = new Razorpay(safeOptions);
    } catch (err) {
      settleReject({ code: 'CHECKOUT_LOAD_FAILED', error: err });
      return;
    }

    rzp.on('payment.failed', (resp) => {
      const err = (resp && resp.error) || {};
      settleReject({
        code: 'CHECKOUT_FAILED',
        error: err,
        // Surface the most useful bits up to the caller's ErrorAlert.
        message:
          err.description ||
          err.reason ||
          'Payment failed at the gateway. Please try again.'
      });
    });

    try {
      rzp.open();
    } catch (err) {
      settleReject({ code: 'CHECKOUT_LOAD_FAILED', error: err });
    }
  });
}

// Helper: turn a checkout rejection into a user-facing error message.
export function describeCheckoutError(err) {
  if (!err || typeof err !== 'object') return 'Payment failed. Please try again.';
  switch (err.code) {
    case 'CHECKOUT_DISMISSED':
      return 'Payment cancelled. You can try again any time.';
    case 'CHECKOUT_FAILED':
      return err.message || 'Payment failed at the gateway. Please try again.';
    case 'CHECKOUT_LOAD_FAILED':
      return 'Could not load the Razorpay checkout — check your network and try again.';
    case 'CHECKOUT_MISSING_KEY':
      // Spec wording: this message is what the operator/developer sees when
      // VITE_RAZORPAY_KEY_ID is absent from the client bundle. Vite needs a
      // restart after any .env edit; HMR will not pick the new key up.
      return 'Razorpay key is missing. Add VITE_RAZORPAY_KEY_ID in frontend .env and restart the frontend dev server.';
    case 'CHECKOUT_BAD_ORDER_ID':
      return 'The payment order reference is missing or malformed. Please refresh and try again.';
    case 'CHECKOUT_BAD_AMOUNT':
      return 'The payment amount is invalid. Please refresh and try again.';
    case 'CHECKOUT_BAD_CURRENCY':
      return 'Currency mismatch — only INR is supported on this account.';
    default:
      return err.message || 'Payment failed. Please try again.';
  }
}
